import { useEffect, useRef } from "react";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════════
// 3. PHOTOLED — vertical photo-led scroll, photos dominate.
//
// Design lead: Bahor (w03) — soft watercolor wedding for Nilufar & Bekzod,
// 21.05.2026, Bukhara.
//
// Bahor signatures layered in here:
//   * Painterly photo treatment — every GuestPhoto sits inside a wrapper that
//     adds a mauve/cream radial wash via mix-blend-multiply.
//   * Scrapbook gallery — the two-photo intro and the section-break photos
//     are taped down with washi-tape pseudo-elements (rotated rectangles).
//   * Hand-drawn dividers — sections are separated by a brushstroke SVG that
//     replaces the default 1px borderTop the section primitives draw.
//   * Breathing watercolor blooms behind hero, story, schedule.
//   * Bukhara silhouette behind the schedule section, ~6% opacity.
//   * Names paint themselves in via clip-path animation on first paint.
//   * Scroll-driven blur-to-sharp reveal on each top-level section.
//   * Sound: section reveal → swoosh, RSVP submit → ding, button taps → tap,
//     sound toggle → music-on/off. All gated by useSound.enabled, which is
//     itself gated by prefers-reduced-motion default.
// ═══════════════════════════════════════════════════════════════════════════

export function VariantPhotoLed({ t, isDark, ctx }: VariantProps) {
  const { play, enabled: soundOn, toggle: toggleSound } = useSound();
  const tt = useT();

  // Detect Bahor specifically — keep the rest of the photoled audience neutral
  // so we don't accidentally style other templates that share the variant.
  const isBahor = t.id === "w03";

  // Watch for RSVP transitions (null → "yes" / "no") so we can ding once.
  const prevRsvp = useRef<string | null>(ctx.rsvp.rsvp);
  useEffect(() => {
    if (prevRsvp.current !== ctx.rsvp.rsvp && ctx.rsvp.rsvp) {
      play("ding");
    }
    prevRsvp.current = ctx.rsvp.rsvp;
  }, [ctx.rsvp.rsvp, play]);

  // Scroll-driven reveals (blur → sharp). Honors reduced motion via CSS.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (typeof IntersectionObserver === "undefined") return;
    const reduce =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      root.querySelectorAll<HTMLElement>("[data-bahor-reveal]").forEach((el) => {
        el.dataset.bahorRevealed = "1";
      });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const el = e.target as HTMLElement;
            if (el.dataset.bahorRevealed === "1") continue;
            el.dataset.bahorRevealed = "1";
            play("swoosh");
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.18 }
    );
    root.querySelectorAll<HTMLElement>("[data-bahor-reveal]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [play]);

  // Delegate `tap` sounds to any <button> click within the variant root.
  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const tgt = e.target as HTMLElement | null;
    if (!tgt) return;
    if (tgt.closest("[data-bahor-sound-toggle]")) return; // handled separately
    if (tgt.closest("button")) play("tap");
  };

  // Style block that also disables animations for prefers-reduced-motion.
  const css = `
    @keyframes bahor-bloom {
      0%, 100% { transform: scale(1);    opacity: 0.32; }
      50%      { transform: scale(1.07); opacity: 0.42; }
    }
    @keyframes bahor-bloom-alt {
      0%, 100% { transform: scale(1.03); opacity: 0.26; }
      50%      { transform: scale(0.94); opacity: 0.34; }
    }
    @keyframes bahor-paint {
      from { clip-path: inset(0 100% 0 0); }
      to   { clip-path: inset(0 0 0 0); }
    }
    @keyframes bahor-reveal {
      from { opacity: 0; filter: blur(10px); transform: translateY(14px); }
      to   { opacity: 1; filter: blur(0);    transform: translateY(0); }
    }
    .bahor-root { position: relative; }
    .bahor-bloom-a { animation: bahor-bloom 8s ease-in-out infinite; transform-origin: center; }
    .bahor-bloom-b { animation: bahor-bloom-alt 8s ease-in-out infinite; transform-origin: center; }
    .bahor-paint   { animation: bahor-paint 2.4s ease-out both;
                     font-feature-settings: "liga","dlig","calt";
                     -webkit-font-smoothing: antialiased; }
    [data-bahor-reveal] { opacity: 0; filter: blur(8px); transform: translateY(10px);
                          transition: none; }
    [data-bahor-reveal][data-bahor-revealed="1"] {
      animation: bahor-reveal 0.95s cubic-bezier(0.2,0.7,0.2,1) both;
    }
    .bahor-photo-wash { position: relative; overflow: hidden; }
    .bahor-photo-wash::after {
      content: ""; position: absolute; inset: 0; pointer-events: none;
      background:
        radial-gradient(120% 90% at 30% 10%, rgba(139,92,126,0.12) 0%, rgba(139,92,126,0) 55%),
        radial-gradient(120% 90% at 80% 90%, rgba(250,244,236,0.30) 0%, rgba(250,244,236,0) 60%);
      mix-blend-mode: multiply;
    }
    .bahor-scrapbook { position: relative; }
    .bahor-scrapbook::before, .bahor-scrapbook::after {
      content: ""; position: absolute; width: 22%; height: 12px;
      background: rgba(139,92,126,0.42); box-shadow: 0 1px 2px rgba(31,29,24,0.18);
      pointer-events: none; z-index: 4;
    }
    .bahor-scrapbook::before { top: -6px;  left: 16%;  transform: rotate(-7deg); }
    .bahor-scrapbook::after  { bottom: -6px; right: 16%; transform: rotate(6deg); }
    .bahor-scrapbook-r::before { background: rgba(231,210,196,0.7); }
    .bahor-scrapbook-r::after  { background: rgba(139,92,126,0.34); }
    .bahor-divider { display: block; width: 100%; height: 18px; }
    .bahor-quote {
      font-family: var(--font-hand);
      font-size: clamp(22px, 2.4vw, 32px);
      color: ${t.accent};
      letter-spacing: 0.005em;
      line-height: 1.25;
    }
    .bahor-sound-btn {
      position: fixed; right: 18px; bottom: 18px; z-index: 50;
      width: 44px; height: 44px; border-radius: 999px;
      background: ${t.bg}; color: ${t.accent};
      border: 1px solid ${t.accent}66;
      box-shadow: 0 6px 18px rgba(31,29,24,0.18);
      display: grid; place-items: center;
      cursor: pointer; transition: transform .2s ease;
      font-family: var(--font-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.18em;
    }
    .bahor-sound-btn:hover { transform: scale(1.05); }
    @media (prefers-reduced-motion: reduce) {
      .bahor-bloom-a, .bahor-bloom-b { animation: none !important; }
      .bahor-paint                   { animation: none !important; clip-path: inset(0 0 0 0); }
      [data-bahor-reveal]            { opacity: 1 !important; filter: none !important; transform: none !important; }
      [data-bahor-reveal][data-bahor-revealed="1"] { animation: none !important; }
    }

    /* Mobile-only / desktop-only utilities (scoped to .bahor-variant) */
    .bahor-variant .mobile-only { display: none; }
    .bahor-variant .desktop-only { display: block; }

    /* Watercolor "stamp" — mobile stationery mark at the very top */
    .bahor-stamp {
      width: 78px; height: 78px; border-radius: 999px;
      display: grid; place-items: center;
      margin: 14px auto 0;
      background: radial-gradient(circle at 35% 30%,
        ${t.accent}55 0%, ${t.accent}38 35%, ${t.accent}1a 65%, ${t.accent}00 100%);
      border: 1px dashed ${t.accent}66;
      font-family: var(--font-ui);
      font-size: 9px; font-weight: 700; letter-spacing: 0.22em;
      color: ${t.ink}cc;
      text-align: center;
      box-shadow: inset 0 0 14px ${t.accent}22;
    }
    .bahor-stamp span { display: block; line-height: 1.35; }
    .bahor-stamp .stamp-dot { font-size: 7px; opacity: 0.7; }

    @media (max-width: 768px) {
      .bahor-variant .mobile-only { display: block; }
      .bahor-variant .desktop-only { display: none; }

      /* 1. Hero — script names: smaller but still hero-feeling, tight leading */
      .bahor-variant .bahor-hero-names {
        font-size: clamp(36px, 12vw, 72px) !important;
        line-height: 0.9 !important;
        letter-spacing: 0 !important;
      }
      .bahor-variant .bahor-hero {
        padding: 64px 18px 48px !important;
        height: auto !important;
        min-height: 100svh !important;
      }
      .bahor-variant .bahor-hero-eyebrow {
        font-size: 30px !important;
      }
      .bahor-variant .bahor-hero-meta {
        font-size: 11px !important;
        letter-spacing: 0.24em !important;
        padding: 11px 18px !important;
      }

      /* 2. Painterly photo washes — tone overlay so faces stay visible */
      .bahor-variant .bahor-photo-wash::after {
        opacity: 0.65;
      }

      /* 3. Scrapbook polaroids — stack vertically, alternating tape, rotation */
      .bahor-variant .bahor-scrap-grid {
        grid-template-columns: 1fr !important;
        gap: 56px !important;
        max-width: 360px !important;
      }
      .bahor-variant .bahor-scrap-grid > div:nth-child(1) {
        margin-top: 0 !important;
        transform: rotate(-2deg) !important;
      }
      .bahor-variant .bahor-scrap-grid > div:nth-child(2) {
        margin-top: 0 !important;
        transform: rotate(2deg) !important;
      }
      .bahor-variant .bahor-scrap-section {
        padding: 72px 24px 64px !important;
      }
      .bahor-variant .bahor-scrap-title {
        font-size: clamp(34px, 9vw, 44px) !important;
      }
      .bahor-variant .bahor-scrap-lede {
        font-size: 16px !important;
        margin-bottom: 48px !important;
      }

      /* 4. Bukhara silhouette — small bottom-right accent */
      .bahor-variant .bahor-bukhara-svg {
        width: 40% !important;
        height: auto !important;
        left: auto !important;
        right: 0 !important;
        bottom: 0 !important;
        transform: none !important;
        opacity: 0.12 !important;
      }

      /* 5. Brushstroke dividers — narrower, hand-drawn feel */
      .bahor-variant .bahor-divider {
        width: 60% !important;
        margin: 0 auto !important;
      }

      /* 6. Pull-quote — smaller, generous padding, eyebrow on top */
      .bahor-variant .bahor-quote {
        font-size: clamp(24px, 6.4vw, 28px) !important;
        font-style: italic;
        padding: 0 12px;
      }
      .bahor-variant .bahor-quote-block {
        padding: 8px 24px 64px !important;
      }
      .bahor-variant .bahor-quote-eyebrow {
        font-size: 10px !important;
        letter-spacing: 0.28em !important;
        margin-bottom: 14px;
        margin-top: 0 !important;
      }

      /* 8. Full-bleed photo break — phone-screen-sized, cinematic */
      .bahor-variant .bahor-photo-break {
        height: 100vh !important;
      }
      .bahor-variant .bahor-photo-break-text {
        font-size: clamp(56px, 16vw, 72px) !important;
        text-shadow: 0 3px 18px rgba(0,0,0,0.55), 0 1px 4px rgba(0,0,0,0.4) !important;
        line-height: 0.9 !important;
      }

      /* Sound toggle — slightly smaller, out of the way */
      .bahor-variant .bahor-sound-btn {
        width: 40px; height: 40px;
        right: 14px; bottom: 14px;
      }
    }

    /* 7. Soft motion alternative — touch-only "pickup" feel on polaroids.
       Lockdown disables animations, so this is a non-animated state shift. */
    @media (hover: none) {
      .bahor-variant .bahor-scrapbook:active {
        box-shadow: 0 18px 38px ${t.ink}3a, 0 4px 10px ${t.ink}26 !important;
      }
    }
  `;

  // Reusable hand-drawn brushstroke divider (cream + accent, two strokes).
  const Brushstroke = ({ flip = false }: { flip?: boolean }) => (
    <svg
      className="bahor-divider"
      viewBox="0 0 1000 24"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ transform: flip ? "scaleX(-1)" : undefined }}
    >
      <path
        d="M5 12 Q 120 4 250 12 T 520 14 T 780 10 T 996 13"
        stroke={`${t.ink}55`}
        strokeWidth="1.3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M40 16 Q 220 22 420 14 T 760 18 T 990 16"
        stroke={`${t.accent}88`}
        strokeWidth="0.9"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );

  // Watercolor blob (procedurally-shaped via SVG filter turbulence).
  const Bloom = ({
    cx, cy, rx, ry, fill, alt = false, seed = 4,
  }: { cx: number; cy: number; rx: number; ry: number; fill: string; alt?: boolean; seed?: number }) => (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      preserveAspectRatio="none"
      viewBox="0 0 200 200"
      aria-hidden="true"
    >
      <defs>
        <filter id={`bahor-edge-${seed}`} x="-30%" y="-30%" width="160%" height="160%">
          <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="2" seed={seed} />
          <feDisplacementMap in="SourceGraphic" scale="6" />
        </filter>
      </defs>
      <g filter={`url(#bahor-edge-${seed})`}>
        <g className={alt ? "bahor-bloom-b" : "bahor-bloom-a"} style={{ transformBox: "fill-box" } as React.CSSProperties}>
          <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={fill} />
        </g>
      </g>
    </svg>
  );

  // Bukhara minaret/madrasah silhouette — Registan-inspired, sits behind the
  // schedule. Three iwans + side minarets, very low opacity, painterly feel.
  const BukharaSilhouette = () => (
    <svg
      className="bahor-bukhara-svg"
      viewBox="0 0 800 240"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "50%",
        bottom: 0,
        transform: "translateX(-50%)",
        width: "min(900px, 92%)",
        height: "60%",
        opacity: 0.08,
        pointerEvents: "none",
      }}
    >
      <g fill={t.ink}>
        {/* Side minaret left */}
        <rect x="60" y="60" width="22" height="180" />
        <ellipse cx="71" cy="60" rx="14" ry="18" />
        <circle cx="71" cy="38" r="4" />
        {/* Madrasah body */}
        <rect x="120" y="120" width="560" height="120" />
        {/* Central iwan (pointed arch) */}
        <path d="M340 240 L 340 150 Q 400 60 460 150 L 460 240 Z" />
        {/* Big dome */}
        <path d="M330 150 Q 400 60 470 150 Q 460 100 400 80 Q 340 100 330 150 Z" />
        <ellipse cx="400" cy="78" rx="70" ry="14" />
        <rect x="395" y="40" width="10" height="40" />
        <circle cx="400" cy="34" r="6" />
        {/* Side iwans */}
        <path d="M170 240 L 170 175 Q 210 130 250 175 L 250 240 Z" />
        <path d="M550 240 L 550 175 Q 590 130 630 175 L 630 240 Z" />
        {/* Side minaret right */}
        <rect x="718" y="60" width="22" height="180" />
        <ellipse cx="729" cy="60" rx="14" ry="18" />
        <circle cx="729" cy="38" r="4" />
      </g>
    </svg>
  );

  return (
    <div ref={rootRef} className="bahor-root bahor-variant" onClickCapture={onClickCapture}>
      <style>{css}</style>

      {/* Mobile-only watercolor "stamp" — tiny stationery mark at the top */}
      <div className="mobile-only" aria-hidden="true">
        <div className="bahor-stamp">
          <div>
            <span>{tt("variant.w03.stamp.line1")}</span>
            <span className="stamp-dot">·</span>
            <span>{tt("variant.w03.stamp.line2")}</span>
          </div>
        </div>
      </div>

      {/* Sound toggle — gentle music-on / music-off accents. */}
      <button
        type="button"
        data-bahor-sound-toggle
        className="bahor-sound-btn"
        aria-label={soundOn ? "Ovozni o'chirish" : "Ovozni yoqish"}
        onClick={() => {
          // Play the *destination* state's transition sound, then flip.
          play(soundOn ? "music-off" : "music-on");
          toggleSound();
        }}
      >
        {soundOn ? "♪" : "—"}
      </button>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        data-bahor-reveal
        className="bahor-hero"
        style={{ height: "100vh", position: "relative", overflow: "hidden" }}
      >
        <div className="bahor-photo-wash" style={{ position: "absolute", inset: 0 }}>
          <GuestPhoto
            src={t.photos[0]}
            style={{ position: "absolute", inset: 0 }}
            fallbackBg={t.bg}
            fallbackTone={t.accent}
          />
        </div>
        {/* Painterly bottom gradient — cream wash, not black */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg,
              rgba(31,29,24,0.06) 0%,
              rgba(31,29,24,0.18) 35%,
              rgba(31,29,24,0.55) 100%)`,
          }}
        />
        {/* Hero-side bloom */}
        <div style={{ position: "absolute", left: "-8%", bottom: "-10%", width: "55%", height: "55%" }}>
          <Bloom cx={100} cy={120} rx={88} ry={56} fill={t.accent} seed={3} />
        </div>
        <div style={{ position: "absolute", right: "-10%", top: "-8%", width: "45%", height: "45%" }}>
          <Bloom cx={100} cy={100} rx={72} ry={50} fill={t.accent} alt seed={7} />
        </div>

        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "0 24px 80px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div className="bahor-hero-eyebrow" style={{ fontFamily: "var(--font-hand)", fontSize: 42, marginBottom: 12, opacity: 0.95 }}>
            {tt("variant.w03.hero.save_the_date")}
          </div>
          <h1
            className={`bahor-hero-names${isBahor ? " bahor-paint" : ""}`}
            style={{
              fontFamily: t.font,
              fontSize: "clamp(72px, 10vw, 140px)",
              fontStyle: "italic",
              fontWeight: 400,
              lineHeight: 0.95,
              letterSpacing: "-0.005em",
              textShadow: "0 4px 24px rgba(0,0,0,0.35)",
            }}
          >
            {t.names}
          </h1>
          <div
            className="bahor-hero-meta"
            style={{
              marginTop: 28,
              padding: "14px 28px",
              border: "1px solid rgba(255,255,255,0.55)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              fontFamily: "var(--font-ui)",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.32em",
            }}
          >
            {t.date} · {t.venue.toUpperCase()}
          </div>
        </div>
      </section>

      <Brushstroke />

      {/* ── Two-photo intro / scrapbook ─────────────────────────────────── */}
      <section
        data-bahor-reveal
        className="bahor-scrap-section"
        style={{ padding: "120px 60px 100px", textAlign: "center", position: "relative", overflow: "hidden" }}
      >
        {/* Soft accent bloom */}
        <div style={{ position: "absolute", left: "-10%", top: "10%", width: "40%", height: "60%" }}>
          <Bloom cx={100} cy={100} rx={60} ry={48} fill={t.accent} seed={11} />
        </div>

        <h2
          className="bahor-scrap-title"
          style={{
            fontFamily: t.font,
            fontSize: "clamp(40px, 5vw, 64px)",
            fontStyle: "italic",
            fontWeight: 400,
            marginBottom: 24,
            position: "relative",
          }}
        >
          {tt("variant.w03.scrap.title")}
        </h2>
        <p
          className="bahor-scrap-lede"
          style={{
            fontFamily: "var(--font-body)",
            fontStyle: "italic",
            fontSize: 19,
            maxWidth: 640,
            margin: "0 auto 80px",
            lineHeight: 1.7,
            color: `${t.ink}cc`,
            position: "relative",
          }}
        >
          {tt("variant.w03.scrap.lede")}
        </p>
        <div
          className="bahor-scrap-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 48,
            maxWidth: 1100,
            margin: "0 auto",
            position: "relative",
          }}
        >
          <div
            className="bahor-scrapbook bahor-photo-wash"
            style={{
              aspectRatio: "4/5",
              transform: "rotate(-1.2deg)",
              boxShadow: `0 12px 32px ${t.ink}26`,
            }}
          >
            <GuestPhoto
              src={t.photos[1]}
              style={{ width: "100%", height: "100%" }}
              fallbackBg={t.bg}
              fallbackTone={t.accent}
            />
          </div>
          <div
            className="bahor-scrapbook bahor-scrapbook-r bahor-photo-wash"
            style={{
              aspectRatio: "4/5",
              marginTop: 60,
              transform: "rotate(1.4deg)",
              boxShadow: `0 12px 32px ${t.ink}26`,
            }}
          >
            <GuestPhoto
              src={t.photos[2]}
              style={{ width: "100%", height: "100%" }}
              fallbackBg={t.bg}
              fallbackTone={t.accent}
            />
          </div>
        </div>
      </section>

      <Brushstroke flip />

      <div data-bahor-reveal>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </div>

      <Brushstroke />

      <div data-bahor-reveal style={{ position: "relative" }}>
        <StorySection
          t={t}
          eyebrow={tt("variant.w03.story.eyebrow")}
          title={tt("variant.w03.story.title")}
          body={tt("variant.w03.story.body")}
        />
        {/* Bukhara pull-quote nudges the story toward place */}
        <div
          aria-hidden={false}
          className="bahor-quote-block"
          style={{
            textAlign: "center",
            padding: "0 24px 80px",
            marginTop: -20,
            position: "relative",
          }}
        >
          {/* On mobile the eyebrow appears above the quote (order); desktop keeps it below */}
          <div
            className="bahor-quote-eyebrow mobile-only"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              letterSpacing: "0.32em",
              color: `${t.ink}99`,
            }}
          >
            {tt("variant.w03.quote.eyebrow")}
          </div>
          <div
            className="bahor-quote"
            style={{ maxWidth: 620, margin: "0 auto" }}
          >
            {tt("variant.w03.quote.text")}
          </div>
          <div
            className="bahor-quote-eyebrow desktop-only"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              letterSpacing: "0.32em",
              color: `${t.ink}99`,
              marginTop: 12,
            }}
          >
            {tt("variant.w03.quote.eyebrow")}
          </div>
        </div>
      </div>

      <Brushstroke />

      {/* ── Full-bleed photo break (scrapbook quote) ────────────────────── */}
      <section
        data-bahor-reveal
        className="bahor-photo-break"
        style={{ height: "60vh", position: "relative", overflow: "hidden" }}
      >
        <div className="bahor-photo-wash" style={{ position: "absolute", inset: 0 }}>
          <GuestPhoto
            src={t.photos[3] || t.photos[0]}
            style={{ position: "absolute", inset: 0 }}
            filter="brightness(0.88) saturate(0.95)"
            fallbackBg={t.bg}
            fallbackTone={t.accent}
          />
        </div>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(60% 60% at 50% 50%, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.35) 100%)`,
          }}
        />
        <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div
            className="bahor-photo-break-text"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: "clamp(48px, 6vw, 88px)",
              color: "#fff",
              textShadow: "0 4px 22px rgba(0,0,0,0.45)",
              textAlign: "center",
              padding: "0 24px",
              letterSpacing: "0.005em",
            }}
          >
            "{t.names.split("&")[0].trim()} & {t.names.split("&")[1]?.trim()}"
          </div>
        </div>
      </section>

      <Brushstroke flip />

      <div data-bahor-reveal>
        <GallerySection t={t} />
      </div>

      <Brushstroke />

      {/* ── Schedule with Bukhara silhouette ────────────────────────────── */}
      <div data-bahor-reveal style={{ position: "relative", overflow: "hidden" }}>
        <BukharaSilhouette />
        <div style={{ position: "absolute", left: "-6%", top: "10%", width: "30%", height: "40%" }}>
          <Bloom cx={100} cy={100} rx={56} ry={42} fill={t.accent} seed={13} />
        </div>
        <ScheduleSection
          t={t}
          items={[
            [tt("variant.w03.schedule.0.time"), tt("variant.w03.schedule.0.event")],
            [tt("variant.w03.schedule.1.time"), tt("variant.w03.schedule.1.event")],
            [tt("variant.w03.schedule.2.time"), tt("variant.w03.schedule.2.event")],
            [tt("variant.w03.schedule.3.time"), tt("variant.w03.schedule.3.event")],
          ]}
        />
      </div>

      <Brushstroke />

      <div data-bahor-reveal>
        <MapSection t={t} isDark={isDark} addressLine={`${t.venue} · ${tt("variant.w03.map.address")}`} />
      </div>

      <Brushstroke />

      <div data-bahor-reveal>
        <RsvpSection t={t} isDark={isDark} {...ctx.rsvp} />
      </div>

      <Brushstroke />

      <div data-bahor-reveal>
        <GuestbookSection t={t} isDark={isDark} {...ctx.guestbook} />
      </div>

      <Brushstroke />

      <div data-bahor-reveal>
        <ShareSection t={t} />
      </div>

      <GuestFooter t={t} />
    </div>
  );
}
